/* NOTES:
-----------------------------------------------------------------------------------------
Coercing

When working with query parameters we often want to have them typed as `number` or `boolean`
or arrays (of strings, numbers, etc.). The values coming from URL, however, are always of
type string | string[] | undefined.

Coercing is used when:
- we want to use other types, e.g. boolean, number, etc. (?isAdmin=true&score=10)
- we want to make sure we don't have an empty values (?username=)
- we wan to make sure we have an array of values (?grades=1&grades=2&grades=3)
- we want to use enums (see below)

export const schema = z.object({
    // boolean
    isAdmin: z.preprocess((val) => coerceBoolean(val), z.boolean())

    // number
	score: z.preprocess((val) => coerceNumber(val), z.number()),

    // disallow empty string
    username: z.preprocess((val) => coerceEmptyStringToNull(val), z.string())

    // array of numbers
    grades: z.preprocess((val) => coerceArrayOfNumbers(val), z.array(z.number()))

    // array of strings
    siblings: z.preprocess((val) => coerceArrayOfStrings(val), z.array(z.string()))
})
-----------------------------------------------------------------------------------------
String enums

For string enums you can use `z.enum`:

const schema = z.object({
    status: z.enum(['active', 'inactive'])
})
-----------------------------------------------------------------------------------------
Number enums

`z.enum` does not support numbers (only strings) ->
use `z.enum` (with coercion) instead.

enum DISCOUNT {
    ZERO = 0,
    TEN = 10,
    TWENTY = 20
}

const schema = z.object({
    discount: z.preprocess((val) => coerceNumber(val), z.enum(DISCOUNT))
})
-----------------------------------------------------------------------------------------
Nullable vs Optional vs Nullish

.nullable()
    null -> null
    undefined -> ERROR
    "xyz" -> "xyz"

.optional()
    null -> ERROR
    undefined -> undefined
    "xyz" -> "xyz"

.nullish()
    null -> null
    undefined -> undefined
    "xyz" -> "xyz"

.nullable().default("abc")
    null -> null
    undefined -> "abc"
    "xyz" -> "xyz"

.optional().default("abc")
    null -> ERROR
    undefined -> "abc"
    "xyz" -> "xyz"

.nullish().default("abc")
    null -> null
    undefined -> "abc"
    "xyz" -> "xyz"
-----------------------------------------------------------------------------------------
*/

import dayjs from 'dayjs'

/**
 * Preprocess helpers (coercing)
 */
export const coerceString = (value: unknown): string | undefined | null => {
	if (value === '') return null
	if (typeof value === 'string') return value

	return undefined
}

export const coerceNumber = (val: unknown): number | undefined | null => {
	if (val === '') return null
	if (typeof val === 'number') return val
	if (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val))) return Number(val)
	return undefined
}

export const coerceStringArray = (val: unknown): (string | undefined | null)[] | undefined | null => {
	if (val === '') return null
	if (Array.isArray(val))
		return val.map((arrayValue) => {
			if (arrayValue === '') return null
			return typeof arrayValue === 'string' ? arrayValue : undefined
		})
	if (typeof val === 'string') return val === '' ? null : [val]
	return undefined
}

export const coerceNumberArray = (val: unknown): (number | undefined | null)[] | undefined | null => {
	if (val === '') return null
	if (Array.isArray(val)) {
		return val.map((arrayValue) => {
			if (arrayValue === '') return null
			return typeof Number(arrayValue) === 'number' ? Number(arrayValue) : undefined
		})
	}
	if (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val))) return [Number(val)]
	return undefined
}

export const coerceBoolean = (val: unknown): boolean | undefined | null => {
	if (val === '') return null
	if (val === 'true' || val === true || val === '1') return true
	if (val === 'false' || val === false || val === '0') return false
	return undefined
}

export const coerceValidDayjs = (val: unknown, format?: string): string | undefined | null => {
	if (val === '') return null
	if (typeof val === 'string') {
		if (format) {
			if (dayjs(val, format, true).isValid()) return val
		} else if (dayjs(val).isValid()) return val
	}
	return undefined
}
